import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  Settings,
  Plus,
  Edit,
  Search,
  CheckCircle,
  XCircle,
  Activity,
  BarChart3,
  Zap,
  RefreshCw,
} from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { StatusModal } from "../../../components/modals/StatusModal";
import { api } from "../../../services/api";

interface Status {
  id: string;
  nome_status: string;
  status: "A" | "I";
  ordem_minima_patente: number;
  id_habbo_variavel?: number | null;
  criado_em: string;
  alterado_em: string;
}

export function Status() {
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [filteredStatus, setFilteredStatus] = useState<Status[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get("/status");
      setStatusList(response.data);
      setFilteredStatus(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar status", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar status quando o termo de busca mudar
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStatus(statusList);
    } else {
      const filtered = statusList.filter((status) =>
        status.nome_status.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStatus(filtered);
    }
  }, [searchTerm, statusList]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Busca já é feita em tempo real pelo useEffect
  };

  const openCreateModal = () => {
    setEditingStatus(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingStatus(null);
  };

  const handleSaveSuccess = () => {
    closeModal();
    fetchStatus();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Settings className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Status
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os status do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Filtros de Busca */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Status
              </CardTitle>
              <CardDescription>
                Digite o nome do status para pesquisar ou deixe em branco para ver todos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Nome do Status</Label>
                  <InputText
                    id="search"
                    name="search"
                    title=""
                    placeholder="Digite o nome do status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                  <Button
                    type="button"
                    onClick={openCreateModal}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Status
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                            <CardDescription>
              {filteredStatus.length} status encontrado(s)
            </CardDescription>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredStatus.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum status encontrado
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredStatus.map((status) => (
                    <div
                      key={status.id}
                      className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center">
                          <Activity className="w-5 h-5 text-[oklch(0.45_0.15_142)]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">{status.nome_status}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={
                              status.status === "A"
                                ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                : "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600"
                            }>
                              {status.status === "A" ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <XCircle className="h-3 w-3 mr-1" />
                              )}
                              {status.status === "A" ? "Ativo" : "Inativo"}
                            </Badge>
                            {status.ordem_minima_patente > 0 && (
                              <Badge className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700">
                                Ordem mínima: {status.ordem_minima_patente}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            Criado em {new Date(status.criado_em).toLocaleDateString('pt-BR')}
                            {" · "}ID Habbo: <span className="font-medium">{status.id_habbo_variavel ?? "N/A"}</span>
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingStatus(status);
                            setIsModalOpen(true);
                          }}
                          className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10 dark:text-[oklch(0.75_0.15_142)] dark:border-[oklch(0.45_0.15_142)]/40 dark:hover:bg-[oklch(0.45_0.15_142)]/20"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total de Status</span>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                    {statusList.length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status Ativos</span>
                  <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                    {statusList.filter(status => status.status === "A").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status Inativos</span>
                  <Badge className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-600">
                    {statusList.filter(status => status.status === "I").length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recentes</span>
                  <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700">
                    {statusList.filter(status => {
                      const created = new Date(status.criado_em);
                      const now = new Date();
                      const diffDays = Math.ceil((now.getTime() - created.getTime()) / (1000 * 3600 * 24));
                      return diffDays <= 30;
                    }).length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ações Rápidas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Ações Rápidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button
                  onClick={openCreateModal}
                  className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Status
                </Button>
                <Button
                  onClick={() => {
                    setSearchTerm("");
                    fetchStatus();
                  }}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <StatusModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editingStatus={editingStatus}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
}
